import { getItem, setItem } from '../../../utils/storage'

const DEFAULT_MAX_STORED_MESSAGES = 30
const DEFAULT_MAX_STORED_MESSAGE_COLLECTIONS = 10
const localStorageKey = 'activityFeedV2_messages'

export type MessageContentProps<T> = {
  id: string
  content: T
  timestamp: number
}

/**
 * Represents a generic message store factory.
 *
 * @template T - The type of the message content.
 */
export class MessageStoreFactory<T> {
  constructor(
    public store: Record<string, MessageContentProps<T>> = {},
    private maxStoredMessages = DEFAULT_MAX_STORED_MESSAGES
  ) {
    this.store = store
    this.maxStoredMessages = maxStoredMessages
  }

  get size(): number {
    return Object.keys(this.store).length
  }

  private getOldestContent(): MessageContentProps<T> | undefined {
    const sortedMessages = Array.from(Object.values(this.store)).sort((a, b) => a.timestamp - b.timestamp)

    return sortedMessages[0]
  }

  private deleteOldContentIfExceedsSizeLimit(): void {
    while (this.size > this.maxStoredMessages) {
      const oldestMessage = this.getOldestContent()
      if (oldestMessage) {
        this.remove(oldestMessage.id)
      }
    }
  }

  save(id: string, content: T): MessageContentProps<T> {
    const timestamp = Date.now()
    const storedContent = this.store[id]

    const newContent: MessageContentProps<T> = {
      id,
      content,
      timestamp,
    }

    this.store[id] = newContent

    if (!storedContent) {
      this.deleteOldContentIfExceedsSizeLimit()
    }
    return newContent
  }

  remove(id: string): void {
    delete this.store[id]
  }
}

type SaveMessageArgs = {
  collectionID: string
  messageID: string
  message: string
}
/**
 * Represents a collection of messages with associated message stores.
 * This collection is used to store and retrieve messages from local storage.
 *
 * The collection ID here would be the user ID.
 * The message ID here would be the entity ID (Shipment/Booking).
 */
export class MessagesCollectionStore extends MessageStoreFactory<MessageStoreFactory<string>> {
  constructor() {
    const storedMessages = getItem<string>(localStorageKey)
    let collections: Record<string, MessageContentProps<MessageStoreFactory<string>>> = {}
    if (storedMessages) {
      collections = JSON.parse(storedMessages)

      // Loop through all stored collections and convert the content to a MessageStoreFactory
      // so that we can use the same methods to save and remove messages
      Object.values(collections).forEach((collection) => {
        if (collection.content) {
          const messages = collection.content.store
          // Convert the content from a plain object to a MessageStoreFactory
          const messageStore = new MessageStoreFactory<string>(messages)

          collection.content = messageStore
        }
      })
    }
    super(collections, DEFAULT_MAX_STORED_MESSAGE_COLLECTIONS)
  }

  getMessage({
    collectionID,
    messageID,
  }: Pick<SaveMessageArgs, 'collectionID' | 'messageID'>): MessageContentProps<string> | undefined {
    return this.store[collectionID]?.content.store[messageID]
  }

  saveMessageToCollection({ collectionID, messageID, message }: SaveMessageArgs): MessageContentProps<string> {
    let collection = this.store[collectionID]?.content

    if (!collection) {
      collection = new MessageStoreFactory<string>()
    }

    const content = collection.save(messageID, message)
    super.save(collectionID, collection)
    this.updateLocalStorage()

    return content
  }

  removeMessageFromCollection({ collectionID, messageID }: Pick<SaveMessageArgs, 'collectionID' | 'messageID'>): void {
    const collection = this.store[collectionID]?.content

    if (collection) {
      collection.remove(messageID)
    }

    this.updateLocalStorage()
  }

  remove(id: string): void {
    super.remove(id)
    this.updateLocalStorage()
  }

  updateLocalStorage(): void {
    setItem(localStorageKey, JSON.stringify(this.store))
  }
}
